/**
 * ************************************
 *
 * @module  Event.saga.js
 * @author  Vignesh D
 * @date    03/11/2020
 * @description Event saga file
 *
 * ************************************
 */
// ----------------------------------------------------------------------------|
//                                 Imports
// ----------------------------------------------------------------------------|
import { call, put } from 'redux-saga/effects';
import { toast } from 'react-toastify';

import ACTIONS from 'store/actions/actionTypes';
import API from 'API';

import { GENERIC } from 'constants.js';

import { getPartsFromUrl } from 'utils/utils';

import { reportAPIError } from 'utils/ErrorHandlingUtils';

// ----------------------------------------------------------------------------|
//                                Utilities
// ----------------------------------------------------------------------------|
/**
 * @description formats data
 *
 * @param {Array} data - Array of Objects
 *
 * @returns {Array}
 */
const formatData = (data = []) => {
  if (typeof data[0] === 'object') {
    return data.map((each) => each.name);
  }
  return data;
};

// ----------------------------------------------------------------------------|
//                              Sagas - Event
// ----------------------------------------------------------------------------|
/**
 * @description handles uploading an image
 *
 * @param {Object} action - object passed from the redux action
 */
function* uploadImage(action) {
  const { IMAGE_UPLOAD_FAIL } = GENERIC;
  const { postData } = window.axios;
  const { file, fileId, photo_metadata } = action.data;

  try {
    const response = yield call(postData, API.placeImage, file, {
      'Content-Type': 'multipart/form-data',
    });

    if (response.status === 200) {
      yield put({
        type: ACTIONS.EVENT.UPLOAD_IMAGE_SUCCESS,
        data: { ...response.data, fileId, photo_metadata },
      });
    } else {
      toast.error(IMAGE_UPLOAD_FAIL);
      yield put({ type: ACTIONS.EVENT.UPLOAD_IMAGE_FAIL });
    }
  } catch (error) {
    toast.error(IMAGE_UPLOAD_FAIL);
    yield put({ type: ACTIONS.EVENT.UPLOAD_IMAGE_FAIL });
  }
}

/**
 * @description handles updating data
 *
 * @param {Object} action - object passed from the redux action
 * @param {String} id - id of action
 */
function* updatedData(data, id) {
  const { status: toBeStatus } = data;
  const { secondPart: currentTab } = getPartsFromUrl(window.location.pathname);
  const currentStatus = currentTab === 'drafts' ? 'draft' : currentTab;

  if (toBeStatus === currentStatus) {
    yield put({ type: ACTIONS.EVENTS.MOVE_TO_TOP, data: { id, data } });
  } else {
    yield put({ type: ACTIONS.EVENTS.REMOVE_EVENT, data: { id } });
  }
}

/**
 * @description saves data to DB
 *
 * @param {Object} action - object passed from the redux action
 */
function* saveData(action) {
  const { EVENT_SAVE_SUCCESS, EVENT_SAVE_FAIL } = GENERIC;
  const { postData, putData } = window.axios;
  const data = { ...action.data };

  data.neighborhood = formatData(data.neighborhood);

  yield put({
    type: ACTIONS.EVENT.BUTTON_DISBALING,
    data: { isBtnDisabled: true },
  });

  try {
    let response;

    if (action.id) {
      // update
      response = yield call(putData, `${API.place}${action.id}`, data);
    } else {
      // create
      response = yield call(postData, API.place, data);
    }

    if (response.status === 200) {
      yield put({
        type: ACTIONS.EVENT.SAVE_DATA_SUCCESS,
        data: { ...response.data },
      });

      yield updatedData(response.data, action.id);

      action.successCB();
      toast.success(EVENT_SAVE_SUCCESS);
    } else {
      reportAPIError('Event Save - HTTP', response.data);

      yield put({ type: ACTIONS.EVENT.SAVE_DATA_FAIL });
      toast.error(EVENT_SAVE_FAIL);
    }
  } catch (error) {
    reportAPIError('Overall Event Save Catch', error);

    yield put({ type: ACTIONS.EVENT.SAVE_DATA_FAIL });
    toast.error(EVENT_SAVE_FAIL);
  } finally {
    yield put({
      type: ACTIONS.EVENT.BUTTON_DISBALING,
      data: { isBtnDisabled: false },
    });
  }
}

// ----------------------------------------------------------------------------|
//                                  Export
// ----------------------------------------------------------------------------|
export { uploadImage, updatedData, saveData };
